import { ArrowUp, ArrowDown, Shuffle, X, ThumbsUp, ThumbsDown, Flag, Clock, GripVertical, Footprints, UserCheck, ExternalLink } from "lucide-react";
import { catInfo, money, PIN_COLORS } from "@/lib/constants";

const Pill = ({ children, className, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full font-mono ${className}`}>{children}</span>
);

const IconBtn = ({ onClick, title, testId, children, active }) => (
  <button type="button" title={title} aria-label={title} data-testid={testId} onClick={onClick}
    className={`p-1.5 rounded-lg transition-colors ${active ? "bg-[#163A2B] text-white" : "text-stone-500 hover:text-stone-900 hover:bg-stone-100"}`}>{children}</button>
);

export const TravelLeg = ({ stop, mode, n }) => (
  <div className="flex items-center gap-2 pl-6 py-1.5 text-xs text-stone-500" data-testid={`travel-leg-${n}`}>
    <Footprints className="w-3.5 h-3.5" /> ~{stop.travel_min} min {mode} · {stop.travel_km} km <span className="text-stone-400">(estimate)</span>
    {stop.wait_min > 0 && <span className="text-[#163A2B] font-semibold">· {stop.wait_min} min free before it starts</span>}
  </div>
);

export const StopCard = ({ stop, index, active, owner, isMe, actions, dragProps }) => {
  const n = index + 1;
  const Icon = catInfo(stop.cat).icon;
  const color = PIN_COLORS[index % PIN_COLORS.length];
  const joinedMe = stop.joined_users?.some((u) => u.id === isMe);
  return (
    <div data-testid={`stop-card-${n}`} onClick={() => actions.select?.(stop.sid)} {...dragProps}
      className={`relative rounded-2xl border bg-white p-4 space-y-3 transition-shadow duration-200 hover:shadow-md ${active ? "border-[#2F7D55] shadow-md" : "border-stone-200"} ${stop.reported ? "opacity-70" : ""}`}>
      <div className="flex gap-3">
        <div className="flex flex-col items-center gap-1">
          <span className="w-8 h-8 rounded-full text-white font-bold flex items-center justify-center text-sm shrink-0" style={{ background: color, fontFamily: "Outfit" }}>{n}</span>
          {owner && <GripVertical className="w-4 h-4 text-stone-300 cursor-grab" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-stone-500 flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {catInfo(stop.cat).label}{stop.cuisine ? ` · ${stop.cuisine.replace(/;/g, ", ")}` : ""}</p>
              <h3 className="font-bold text-lg leading-tight text-stone-900 truncate" data-testid={`stop-name-${n}`}>{stop.name}</h3>
            </div>
            <span className="font-mono text-sm font-semibold text-stone-800 whitespace-nowrap" data-testid={`stop-time-${n}`}>{stop.arrive}–{stop.depart}</span>
          </div>
          {stop.why && <p className="mt-1.5 text-sm text-stone-600 leading-relaxed" data-testid={`stop-why-${n}`}>{stop.why}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Pill testId={n === 1 ? "honest-label-estimated-price" : `stop-price-${n}`} className="bg-slate-100 text-slate-700">{money(stop.cost_min, stop.cost_max)} {stop.anchor ? "· your estimate" : "· est."}</Pill>
        {stop.hours ? (
          <Pill testId={`stop-hours-${n}`} className="bg-[#E3EFE6] text-[#0F2A1F]"><Clock className="w-3 h-3" /> Listed: {stop.hours.length > 34 ? `${stop.hours.slice(0, 34)}…` : stop.hours}</Pill>
        ) : !stop.anchor && (
          <Pill testId={n === 1 ? "honest-label-unconfirmed-hours" : `stop-hours-unconfirmed-${n}`} className="bg-amber-50 text-amber-800 border border-amber-200"><Clock className="w-3 h-3" /> Hours not confirmed, check before going</Pill>
        )}
        <Pill testId={`stop-booking-${n}`} className="bg-stone-100 text-stone-600">{stop.booking}</Pill>
        {stop.wheelchair === "yes" && <Pill className="bg-sky-50 text-sky-800">Wheelchair: yes (listed)</Pill>}
        {stop.reported && <Pill testId={`stop-reported-${n}`} className="bg-red-50 text-red-700">Reported: {stop.reported}. Consider replacing.</Pill>}
      </div>
      {stop.joined_users?.length > 0 && (
        <p className="text-xs text-[#163A2B] font-semibold flex items-center gap-1" data-testid={`stop-joined-${n}`}><UserCheck className="w-3.5 h-3.5" /> In: {stop.joined_users.map((u) => u.name).join(", ")}</p>
      )}
      <div className="flex items-center gap-0.5 border-t border-stone-100 pt-2 -mb-1" onClick={(e) => e.stopPropagation()}>
        {owner ? (
          <>
            <IconBtn title="Move up" testId={`btn-reorder-up-stop-${n}`} onClick={() => actions.move(index, -1)}><ArrowUp className="w-4 h-4" /></IconBtn>
            <IconBtn title="Move down" testId={`btn-reorder-down-stop-${n}`} onClick={() => actions.move(index, 1)}><ArrowDown className="w-4 h-4" /></IconBtn>
            {!stop.anchor && <IconBtn title="Replace with an alternative" testId={`btn-replace-stop-${n}`} onClick={() => actions.replace(stop)}><Shuffle className="w-4 h-4" /></IconBtn>}
            <IconBtn title="Skip this stop" testId={`btn-skip-stop-${n}`} onClick={() => actions.skip(stop.sid)}><X className="w-4 h-4" /></IconBtn>
            <span className="mx-1 h-4 w-px bg-stone-200" />
            <IconBtn title="Good pick" testId={`btn-like-stop-${n}`} active={stop.feedback === "up"} onClick={() => actions.like(stop)}><ThumbsUp className="w-4 h-4" /></IconBtn>
            <IconBtn title="Not for me" testId={`btn-dislike-stop-${n}`} active={stop.feedback === "down"} onClick={() => actions.dislike(stop)}><ThumbsDown className="w-4 h-4" /></IconBtn>
          </>
        ) : (
          <button data-testid={`btn-im-in-stop-${n}`} onClick={() => actions.join(stop.sid)}
            className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors ${joinedMe ? "bg-[#163A2B] text-white" : "bg-[#E3EFE6] text-[#0F2A1F] hover:bg-[#cfe3d5]"}`}>
            {joinedMe ? "You're in ✓" : "I'm in"}
          </button>
        )}
        <span className="ml-auto flex items-center">
          {/^https?:\/\//i.test(stop.website || "") && <a href={stop.website} target="_blank" rel="noopener noreferrer" data-testid={`stop-website-${n}`} className="p-1.5 text-stone-500 hover:text-stone-900"><ExternalLink className="w-4 h-4" /></a>}
          {!stop.anchor && <IconBtn title="Report a problem" testId={`btn-report-problem-stop-${n}`} onClick={() => actions.report(stop)}><Flag className="w-4 h-4" /></IconBtn>}
        </span>
      </div>
    </div>
  );
};
